/* Spotlight Tenant Home — cinematic dark, theatrical, marquee.

   Emotional intent: DRAMA. Single 100vh full-bleed photo with only a tiny
   "lower-third" title card at the bottom (cinema title card style). No
   chrome above the fold. Below, "NOW SHOWING" with cast-list typography.
   Services rendered as "FEATURED PRESENTATIONS" with marquee cards.
   Reviews as "FROM THE CRITICS" with massive stars.

   Structurally distinct: 100vh photo (NOT letterboxed like Vista, NOT
   bottom-aligned like Editorial), zero top chrome, lower-third overlay,
   cast-list service treatment with massive stars on testimonials. */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star } from "lucide-react";
import { api } from "@/lib/api";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { tenantHref } from "@/lib/tenant";
import { optimizeImageUrl, optimizedSrcSet } from "@/lib/imageOptim";
import { EditorialSkeleton } from "../editorial/_EditorialSkeleton";

const FALLBACK = "https://images.unsplash.com/photo-1503342217505-b0a15ec3261c?auto=format&fit=crop&w=2400&q=80";

export default function SpotlightTenantHome() {
  const { settings, loading } = useSiteSettings();
  const studioName = settings?.studio_name || "Studio";
  const heroImg = settings?.hero_image || settings?.portfolio_images?.[0] || FALLBACK;
  const heroPos = `${settings?.hero_focal_x ?? 50}% ${settings?.hero_focal_y ?? 50}%`;
  const headline = settings?.hero_title_line1 || "A Picture";
  const subhead = settings?.hero_title_line2 || "by " + studioName;
  const intro = settings?.hero_description || `${studioName} — sports, performance, music, and the high-drama editorial work that belongs on a movie marquee.`;
  const aboutTitle = settings?.about_title || "The director.";
  // about_body fallback — kept deliberately generic (no specific
  // experience-years claim like "Twelve years behind the lens" that
  // would be misleading for a freshly-onboarded studio that hasn't
  // filled their About yet). Each Spotlight tenant should override
  // this in admin → Branding → About.
  const aboutBody = settings?.about_body || "Cinematic stills, the way a film stills photographer works on set — composed, lit, edited like a single frame from the picture you're making. Tell us the role and we'll show up with the kit.";

  const [services, setServices] = useState([]);
  const [reviews, setReviews] = useState([]);
  useEffect(() => {
    api.get("/services").then((r) => setServices(Array.isArray(r.data) ? r.data.slice(0, 3) : [])).catch(() => {});
    api.get("/testimonials").then((r) => setReviews(Array.isArray(r.data) ? r.data.slice(0, 3) : [])).catch(() => {});
  }, []);
  useEffect(() => { document.title = `${studioName} — Now Showing`; }, [studioName]);

  // Per-template stamps (Spotlight's marquee chrome) — editable in Site
  // Settings → Template-specific so a studio can dial up the cinema
  // staging ("FEATURE №07", "A PREMIERE BY YOU").
  const tf = settings?.template_fields?.spotlight || {};
  const featureNo = (tf.feature_number || "01").toString();
  const nowShowing = (tf.now_showing || "Now Showing").toString();

  if (loading && !settings) return <EditorialSkeleton eyebrow="Lights down." />;

  return (
    <div data-testid="spotlight-tenant-home"
      style={{ background: "var(--tenant-bg, #0A0A0F)", color: "var(--tenant-fg, #F5E9C9)" }}>

      {/* ── HERO — 100vh full-bleed + lower-third cinema title card ──── */}
      <section className="relative w-full h-screen overflow-hidden" data-testid="spotlight-hero">
        <img src={optimizeImageUrl(heroImg, { w: 2400 })}
          srcSet={optimizedSrcSet(heroImg, [1200, 1800, 2400]) || undefined}
          sizes="100vw"
          alt={`${studioName} feature`}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: heroPos }}
          loading="eager" fetchPriority="high" decoding="async" />
        {/* Vignette */}
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.85) 100%)" }} aria-hidden />
        {/* Top stamp */}
        <div className="absolute top-8 left-8 right-8 flex justify-between text-[9px] uppercase tracking-[0.5em]" style={{ color: "var(--tenant-accent)" }}>
          <span>✦ {nowShowing}</span>
          <span>Feature №{featureNo}</span>
        </div>
        {/* Lower-third cinema title card */}
        <div className="absolute bottom-0 left-0 right-0 px-6 lg:px-16 pb-16 pt-32"
             style={{ background: "linear-gradient(to top, rgba(0,0,0,0.9) 0%, transparent 100%)" }}>
          <div className="max-w-5xl">
            <div className="text-[10px] uppercase tracking-[0.5em] mb-6" style={{ color: "var(--tenant-accent)" }}>
              ✦ A {studioName} Picture
            </div>
            <h1 className="leading-[0.95] uppercase"
                style={{ fontFamily: "var(--tenant-serif)", fontSize: "clamp(3rem, 9vw, 8rem)", letterSpacing: "-0.02em" }}>
              {headline}
              <br />
              <span style={{ color: "var(--tenant-accent)" }}>{subhead}</span>
            </h1>
            <p className="mt-6 max-w-xl text-sm leading-relaxed" style={{ color: "color-mix(in srgb, var(--tenant-fg) 80%, transparent)" }}>
              {intro}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to={tenantHref("/booking")} data-testid="spotlight-hero-book"
                className="inline-flex items-center gap-3 px-7 py-3.5 text-[10px] uppercase tracking-[0.42em] transition-colors"
                style={{ background: "var(--tenant-accent)", color: "var(--tenant-bg)" }}>
                ✦ Book the feature
              </Link>
              <Link to={tenantHref("/portfolio")} className="text-[10px] uppercase tracking-[0.42em] border-b pb-0.5"
                    style={{ color: "var(--tenant-accent)", borderColor: "var(--tenant-accent)" }}>
                Past features →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── THE DIRECTOR (about) ─────────────────────────────────────── */}
      <section className="px-6 lg:px-16 py-24">
        <div className="max-w-3xl mx-auto text-center">
          <div className="text-[10px] uppercase tracking-[0.5em] mb-5" style={{ color: "var(--tenant-accent)" }}>
            ✦ The Director
          </div>
          <h2 className="uppercase leading-[0.95]"
              style={{ fontFamily: "var(--tenant-serif)", fontSize: "clamp(2rem, 5vw, 3.5rem)" }}>
            {aboutTitle}
          </h2>
          <p className="mt-8 text-base leading-loose max-w-xl mx-auto" style={{ color: "color-mix(in srgb, var(--tenant-fg) 75%, transparent)" }}>
            {aboutBody}
          </p>
        </div>
      </section>

      {/* ── FEATURED PRESENTATIONS — marquee cards ──────────────────── */}
      {services.length > 0 && (
        <section className="px-6 lg:px-16 py-24 border-t" style={{ borderColor: "color-mix(in srgb, var(--tenant-accent) 35%, transparent)" }} data-testid="spotlight-services">
          <div className="max-w-[1280px] mx-auto">
            <div className="text-center mb-16">
              <div className="text-[10px] uppercase tracking-[0.5em] mb-5" style={{ color: "var(--tenant-accent)" }}>
                ✦ Featured Presentations
              </div>
              <h2 className="uppercase" style={{ fontFamily: "var(--tenant-serif)", fontSize: "clamp(1.75rem, 4vw, 2.75rem)" }}>
                Three to book this season.
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px" style={{ background: "color-mix(in srgb, var(--tenant-accent) 35%, transparent)" }}>
              {services.map((svc, i) => (
                <div key={svc.id} className="p-10 text-center" style={{ background: "var(--tenant-bg)" }}>
                  <div className="text-[10px] uppercase tracking-[0.5em] mb-4" style={{ color: "var(--tenant-accent)" }}>
                    ✦ {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="text-2xl uppercase mb-4 leading-tight" style={{ fontFamily: "var(--tenant-serif)" }}>
                    {svc.name}
                  </h3>
                  {svc.short_description && (
                    <p className="text-sm leading-relaxed mb-6" style={{ color: "color-mix(in srgb, var(--tenant-fg) 70%, transparent)" }}>{svc.short_description}</p>
                  )}
                  <Link to={tenantHref(`/services#${svc.id || svc.slug || ""}`)}
                    className="inline-block text-[10px] uppercase tracking-[0.42em] border-b pb-0.5"
                    style={{ color: "var(--tenant-accent)", borderColor: "var(--tenant-accent)" }}>
                    Reserve seat →
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── FROM THE CRITICS (reviews) ───────────────────────────────── */}
      {reviews.length > 0 && (
        <section className="px-6 lg:px-16 py-24 border-t" style={{ borderColor: "color-mix(in srgb, var(--tenant-accent) 35%, transparent)" }}>
          <div className="max-w-4xl mx-auto text-center">
            <div className="text-[10px] uppercase tracking-[0.5em] mb-5" style={{ color: "var(--tenant-accent)" }}>
              ✦ From the Critics
            </div>
            <div className="flex justify-center gap-2 mb-8">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star key={n} className="w-7 h-7 fill-current" style={{ color: "var(--tenant-accent)" }} strokeWidth={0} />
              ))}
            </div>
            <blockquote className="italic leading-tight"
                       style={{ fontFamily: "var(--tenant-serif)", fontSize: "clamp(1.5rem, 3.5vw, 2.75rem)" }}>
              “{reviews[0]?.body || reviews[0]?.message || "A masterclass in light and presence."}”
            </blockquote>
            <div className="mt-8 text-[10px] uppercase tracking-[0.42em]" style={{ color: "var(--tenant-fg-soft)" }}>
              — {reviews[0]?.author_name || reviews[0]?.name || "Anonymous Critic"}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
